package com.bikompanion.ui.home

import dev.zacsweers.metro.Assisted
import dev.zacsweers.metro.AssistedFactory
import dev.zacsweers.metro.AssistedInject
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.PARKING_CELL_DEGREES
import com.bikompanion.domain.ParkingCell
import com.bikompanion.domain.ParkingRepository
import com.bikompanion.domain.catchingRequest
import com.bikompanion.domain.usecase.SelectParkingCellsUseCase
import com.bikompanion.ui.mapper.clusterParkingSpots
import kotlin.math.floor
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeMark
import kotlin.time.TimeSource

/**
 * Below this zoom the viewport covers more ground than one round of rack loading is worth.
 *
 * At this zoom a screen is a couple of cells across ([PARKING_CELL_DEGREES]). Pulled back further
 * the count climbs fast, and the individual racks stop being distinguishable anyway — the area
 * markers say where they are instead.
 *
 * It must not sit above the zoom the map opens at, which is 12. Raised to 13 once, it meant the
 * very first thing a rider sees loaded nothing at all — the layer looked entirely dead, and
 * nothing about the toggle suggested that zooming in was what it wanted.
 */
internal const val PARKING_FETCH_ZOOM = 12.0

/**
 * Cells resolved at once.
 *
 * Racks come out of a local file now, so this is only about not decoding the whole screen on one
 * coroutine. It also caps what the fallback path can put on a public Overpass instance, which
 * gives an address two request slots and refuses the rest.
 */
private const val ParkingFetchConcurrency = 2

/**
 * How long a cell that failed is left alone, by consecutive failure.
 *
 * Only the fallback path can fail — the bundled dataset either opens or does not — but when it
 * does, this is what stops the map from making it worse. The camera reports a move every 500 ms
 * and a failed cell is deliberately not recorded as loaded, so without this every pan re-asked
 * for the same cell, which against an instance that is already throttling us is both futile and
 * the very thing keeping the throttle alive. Backing off means the next *look* retries, which was
 * always the intent, rather than the next twitch of the map.
 */
private val ParkingRetryBackoff = listOf(30.seconds, 2.minutes, 10.minutes)

/** Loads the racks under the camera and keeps their on-map grouping current. */
@AssistedInject
class ParkingRackLoader(
    @Assisted private val session: HomeSession,
    private val parkingRepository: ParkingRepository,
    private val selectParkingCells: SelectParkingCellsUseCase,
) {
    @AssistedFactory
    fun interface Factory {
        fun create(session: HomeSession): ParkingRackLoader
    }

    private val state get() = session.state

    /** The camera's zoom, which decides both whether to load and how racks are grouped. */
    private var currentZoom: Double = 0.0

    /** The ground the camera currently frames, which is what rack cells are worked out from. */
    private var currentBounds: BoundingBox? = null

    /** Rack cells being fetched, so a camera move mid-fetch doesn't ask for the same one again. */
    private val inFlight = mutableSetOf<ParkingCell>()

    /** When a failed cell may be asked for again, and how many times running it has failed. */
    private val retryAfter = mutableMapOf<ParkingCell, TimeMark>()
    private val failures = mutableMapOf<ParkingCell, Int>()

    fun onCameraMoved(
        zoom: Double,
        bounds: BoundingBox?,
    ) {
        val crossedZoomLevel = floor(zoom) != floor(currentZoom)
        currentZoom = zoom
        currentBounds = bounds
        // The cluster grid is sized per whole zoom level, so a pan or a nudge of the pinch
        // changes nothing and the racks are left grouped exactly as they were.
        if (crossedZoomLevel) publishAreas()
    }

    /**
     * Loads racks for the ground the camera frames that hasn't been loaded this session.
     *
     * Whole cells rather than the viewport, so two viewports over the same ground ask the same
     * question and the second one is already in hand — a box drawn from the camera would shift
     * with every pan and match nothing. See [SelectParkingCellsUseCase] for which cells a view gets.
     *
     * Scoped to ground rather than to a network on purpose: racks are OpenStreetMap's, not any
     * operator's, and they exist all over the world in places no bike-share network reaches.
     */
    suspend fun loadForVisible() {
        val bounds = currentBounds ?: return
        // Pulled back far enough and the viewport spans more ground than is worth asking for in
        // one go. What has already been fetched still draws — see ParkingAreaSummary — so the
        // map keeps saying where racks are, just not down to the individual rack.
        if (currentZoom < PARKING_FETCH_ZOOM) return
        val pending = selectParkingCells(bounds).filter { cell ->
            cell !in state.value.parkingByCell && cell !in inFlight && isRetryDue(cell)
        }
        if (pending.isEmpty()) return
        inFlight += pending
        state.update { it.copy(parkingLoadFailed = false) }
        publishLoading()
        var answered = false
        try {
            // Two at a time: Overpass gives an address two request slots and refuses the rest,
            // so firing a screenful at once would mostly collect rate-limit errors.
            for (group in pending.chunked(ParkingFetchConcurrency)) {
                coroutineScope {
                    group.map { cell -> async { loadCell(cell) } }.awaitAll()
                }.forEach { loaded -> answered = answered || loaded }
            }
        } finally {
            inFlight -= pending.toSet()
            publishLoading()
        }
        // Nothing at all came back, so say so. A vanishing spinner is indistinguishable from a
        // place with no racks in it, which is the wrong thing to tell somebody looking for one.
        if (!answered) state.update { it.copy(parkingLoadFailed = true) }
    }

    /** Asks again now for whatever the camera is looking at, ignoring any backoff it is under. */
    fun retry() {
        val cells = currentBounds?.let { selectParkingCells(it) }.orEmpty()
        retryAfter -= cells.toSet()
        failures -= cells.toSet()
        session.scope.launch { loadForVisible() }
    }

    /** Fetches one cell, reporting whether it answered. */
    private suspend fun loadCell(cell: ParkingCell): Boolean {
        val spots = catchingRequest { parkingRepository.fetchBikeParking(cell.bounds) }.getOrNull()
        inFlight -= cell
        // Left absent rather than stored empty on failure, so a later look tries again instead
        // of showing "no racks here" for as long as the screen is open.
        if (spots == null) {
            recordFailure(cell)
        } else {
            failures -= cell
            retryAfter -= cell
            state.update { it.copy(parkingByCell = it.parkingByCell + (cell to spots)) }
            publishAreas()
        }
        publishLoading()
        return spots != null
    }

    /**
     * Regroups the loaded racks into the blobs drawn below [PARKING_SPOTS_ZOOM].
     *
     * Here rather than in the draw phase because it walks every rack loaded — up to ten thousand
     * in one cell — and the draw phase runs on every frame of a drag. It only has to be redone
     * when the racks change or the map crosses a whole zoom level, which a pan never does.
     */
    private fun publishAreas() {
        val areas = clusterParkingSpots(state.value.parkingByCell, currentZoom)
        state.update { if (it.parkingAreas == areas) it else it.copy(parkingAreas = areas) }
    }

    private fun isRetryDue(cell: ParkingCell): Boolean = retryAfter[cell]?.hasPassedNow() ?: true

    private fun recordFailure(cell: ParkingCell) {
        val consecutive = (failures[cell] ?: 0) + 1
        failures[cell] = consecutive
        val backoff = ParkingRetryBackoff[minOf(consecutive, ParkingRetryBackoff.size) - 1]
        retryAfter[cell] = TimeSource.Monotonic.markNow() + backoff
    }

    private fun publishLoading() {
        val loading = inFlight.isNotEmpty()
        state.update { if (it.isLoadingParking == loading) it else it.copy(isLoadingParking = loading) }
    }
}
